// Location Service - Handles automatic location detection for guest users
import BookMyOpAPI from './BookMyOpapi';
import { Capacitor } from '@capacitor/core';
import { reverseGeocode } from './googleMapsService';
let Geolocation, Permissions;
try { 
  Geolocation = require('@capacitor/geolocation').Geolocation; 
  Permissions = require('@capacitor/permissions').Permissions; 
} catch { 
  Geolocation = undefined;
  Permissions = undefined;
}

class LocationService {
  /**
   * Request and set user location for session (guest users)
   * @returns {Promise<Object>} Location data or error
   */
  async requestAndSetLocation() {
    const isNative = Capacitor.isNativePlatform && Capacitor.isNativePlatform();
    if (isNative && Geolocation && Permissions) {
      // Native flow: proactively request permissions
      let permStatus;
      try {
        permStatus = await Permissions.query({ name: 'geolocation' });
      } catch (e) {
        permStatus = { state: 'prompt' };
      }
      if (permStatus.state !== 'granted') {
        try {
          permStatus = await Permissions.request({ name: 'geolocation' });
        } catch (e) {
          // Permission query/request failed, treat as denied
          throw new Error('Location permission is denied. Please enable location access for BookMyOP in your device settings.');
        }
      }
      if (permStatus.state !== 'granted') {
        throw new Error('Location permission is denied. Please enable location access for BookMyOP in your device settings.');
      }
      try {
        const position = await Geolocation.getCurrentPosition({ enableHighAccuracy: true });
        const latitude = parseFloat(position.coords.latitude.toFixed(6));
        const longitude = parseFloat(position.coords.longitude.toFixed(6));
        
        // Reverse geocode to get address, city, state, and pincode
        let locationDetails = { latitude, longitude };
        try {
          const geocodedData = await reverseGeocode(latitude, longitude);
          locationDetails = {
            latitude: geocodedData.latitude,
            longitude: geocodedData.longitude,
            address: geocodedData.address,
            city: geocodedData.city,
            state: geocodedData.state,
            pincode: geocodedData.pincode
          };
        } catch (geocodeError) {
          console.warn('⚠️ Reverse geocoding failed, sending only coordinates:', geocodeError);
          // Continue with just lat/long if geocoding fails
        }
        
        // Send to backend session location endpoint (NO AUTH REQUIRED)
        // Send same payload structure as address selection
        const response = await BookMyOpAPI.setSessionLocation(locationDetails);
        // Save location data to session storage for display in Navbar
        const locationData = response.data || response;
        if (locationData) {
          sessionStorage.setItem('guest_location_data', JSON.stringify(locationData));
        }
        return { latitude, longitude, accuracy: position.coords.accuracy, ...response };
      } catch (error) {
        // Could be permission revoked after request, or other
        if (error && error.message && error.message.toLowerCase().includes('permission')) {
          throw new Error('Location permission is denied. Please enable location access for BookMyOP in your device settings.');
        }
        // All other errors
        console.error('❌ Error setting session location (native/capacitor):', error);
        throw error;
      }
    } else {
      return new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
          reject(new Error('Geolocation is not supported by your browser'));
          return;
        }
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            try {
              const latitude = parseFloat(position.coords.latitude.toFixed(6));
              const longitude = parseFloat(position.coords.longitude.toFixed(6));
              
              // Reverse geocode to get address, city, state, and pincode
              let locationDetails = { latitude, longitude };
              try {
                const geocodedData = await reverseGeocode(latitude, longitude);
                locationDetails = {
                  latitude: geocodedData.latitude,
                  longitude: geocodedData.longitude,
                  address: geocodedData.address,
                  city: geocodedData.city,
                  state: geocodedData.state,
                  pincode: geocodedData.pincode
                };
              } catch (geocodeError) {
                console.warn('⚠️ Reverse geocoding failed, sending only coordinates:', geocodeError);
                // Continue with just lat/long if geocoding fails
              }
              
              // Send same payload structure as address selection
              const response = await BookMyOpAPI.setSessionLocation(locationDetails);
              const locationData = response.data || response;
              if (locationData) {
                sessionStorage.setItem('guest_location_data', JSON.stringify(locationData));
              }
              resolve({ latitude, longitude, accuracy: position.coords.accuracy, ...response });
            } catch (error) {
              console.error('❌ Error setting session location:', error);
              reject(error);
            }
          },
          (error) => {
            let errorMessage;
            switch (error.code) {
              case error.PERMISSION_DENIED:
                errorMessage = 'Location permission denied'; break;
              case error.POSITION_UNAVAILABLE:
                errorMessage = 'Location information unavailable'; break;
              case error.TIMEOUT:
                errorMessage = 'Location request timed out'; break;
              default:
                errorMessage = 'Unknown error occurred';
            }
            reject(new Error(errorMessage));
          },
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 300000 // Cache for 5 minutes
          }
        );
      });
    }
  }

  /**
   * Check if location permission is granted
   * @returns {Promise<string>} 'granted', 'denied', or 'prompt'
   */
  async checkLocationPermission() {
    if (!navigator.permissions) {
      return 'unknown';
    }

    try {
      const result = await navigator.permissions.query({ name: 'geolocation' });
      return result.state; // 'granted', 'denied', or 'prompt'
    } catch (error) {
      return 'unknown';
    }
  }

  /**
   * Request location silently (no user-facing errors)
   * Good for initial app load
   */
  async requestLocationSilently() {
    try {
      const permission = await this.checkLocationPermission();
      
      // Only auto-request if permission already granted
      // Don't prompt user on first load
      if (permission === 'granted') {
        await this.requestAndSetLocation();
        return true;
      }
      
      return false;
    } catch (error) {
      console.warn('Silent location request failed:', error);
      return false;
    }
  }
}

export default new LocationService();

